import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import moment from "moment";

import {
  StyledHR,
  BlueTitle,
  ButtonGroupStart,
  RequiredFields,
  TextError,
  LoadingOverlay,
  LoadingGif,
  ModalBodyContainer,
  ModalContent,
  ModalBody,
  ModalContainer,
  ModalHeader,
  ModalFooter,
} from "../../shared/styles/global.styles";
import { ContribuinteName, ModalForm } from "./dependenteModal.styles";
import {
  CreateDependenteDto,
  GetDependenteDto,
  UpdateDependenteDto,
} from "../../shared/dtos/Dependente.dto";
import contribuinteService from "../../services/ContribuinteService";
import dependenteService from "../../services/DependenteService";
import { isValidCPF } from "../ValidateCpf/ValidateCpf";
import InputComponent from "../Input/input";
import Button from "../Button/button";
import { Label } from "../Label/styles";
import SelectComponent from "../Select/select";
import { IoCloseSharp } from "react-icons/io5";
import loadSCGif from "../../assets/load_SC.gif";
import { Col, Row } from "react-bootstrap";

interface Props {
  show: boolean;
  onHide: () => void;
  dependenteData?: GetDependenteDto | null;
  idContribuinte: number;
  onSuccess: (dependente: any | null) => void;
  setNewDependente: React.Dispatch<
    React.SetStateAction<GetDependenteDto | undefined>
  >;
}

const DependenteModal: React.FC<Props> = ({
  show,
  onHide,
  dependenteData,
  idContribuinte,
  onSuccess,
  setNewDependente,
}) => {
  const [cpfCin, setCpfCin] = useState<string>(""); // Inicia com uma string vazia
  const [nome, setNome] = useState<string>("");
  const [sexo, setSexo] = useState<number>(0); // Inicialize com 0 ou qualquer valor numérico padrão
  const [rg, setRg] = useState<string>("");
  const [dataNascimento, setDataNascimento] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [telefoneResidencial, setTelefoneResidencial] = useState<string>("");
  const [telefoneComercial, setTelefoneComercial] = useState<string>("");
  const [telefoneRecado, setTelefoneRecado] = useState<string>("");
  const [celular, setCelular] = useState<string>("");
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [nomeContribuinte, setNomeContribuinte] = useState<string>(""); // Estado para o nome do contribuinte
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const camposObrigatorios: { nome: string; label: string }[] = [
    { nome: "nome", label: "Nome" },
  ];
  const [isRequired, setIsRequired] = useState<boolean>(false);
  const [isRequest, setIsRequest] = useState<boolean>(false);

  const clearError = async (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setInvalidFields((prev) => prev.filter((field) => field !== name));
  };

  const handleSubmit = async () => {
    try {
      setIsRequired(true);
      // Validação dos campos obrigatórios
      const camposVazios = camposObrigatorios.filter((campo) => {
        const valor = eval(campo.nome); // Avalia a variável pelo nome
        const inputElement = document.getElementById(
          campo.nome
        ) as HTMLInputElement; // Obtém o elemento do input

        if (!valor || valor.toString().trim() === "") {
          // Adiciona a classe de erro se o campo estiver vazio e o formulário foi enviado
          inputElement?.classList.add("input-error");
          return true;
        }
        // Remove a classe de erro caso o campo esteja preenchido
        inputElement?.classList.remove("input-error");
        return false;
      });

      // Se houver campos vazios, exibe o alerta e interrompe o envio
      if (camposVazios.length > 0) {
        const mensagens = camposVazios.map((campo) => campo.label).join(", ");
        Swal.fire(
          "Atenção",
          `Os seguintes campos são obrigatórios: ${mensagens}`,
          "warning"
        );
        setInvalidFields((prev) => [...prev, "nome"]);
        return; // Interrompe o envio se houver campos vazios
      }

      setIsFormSubmitted(true); // Marca o formulário como enviado (chamada única)

      Swal.fire({
        title: "Aguarde",
        text: "Tentando cadastrar dependente, por favor aguarde",
        icon: "info",
        showConfirmButton: false,
        allowOutsideClick: false,
      });

      // Processo de atualização ou criação de dependente
      if (dependenteData) {
        const dependenteDataUpdate: UpdateDependenteDto = {
          cpfCin,
          nome,
          sexo,
          rg,
          dataNascimento: new Date(dataNascimento),
          email,
          telefoneResidencial,
          telefoneComercial,
          telefoneRecado,
          celular,
          idContribuinte: dependenteData.idContribuinte,
        };
        await dependenteService.updateDependente(
          dependenteData.id!,
          dependenteDataUpdate
        );
        onSuccess(dependenteDataUpdate);
      } else {
        const dependenteDataCreate: CreateDependenteDto = {
          cpfCin,
          nome,
          sexo,
          rg,
          dataNascimento: new Date(dataNascimento),
          email,
          telefoneResidencial,
          telefoneComercial,
          telefoneRecado,
          celular,
          idContribuinte: idContribuinte,
        };

        await dependenteService
          .createDependente(dependenteDataCreate)
          .then((response) => {
            setNewDependente(response.dependente);
            onSuccess(response.dependente);
          });
      }
      setIsFormSubmitted(false);

      handleClose();
    } catch (error: any) {
      console.error(error);
      setIsRequired(false);
      const errorMessage =
        error.response?.data?.message ||
        "Erro inesperado. Por favor, tente novamente.";
      Swal.fire("Erro", errorMessage, "error");
    } finally {
      setIsRequired(false);
    }
  };

  const resetFields = () => {
    setNome("");
    setCpfCin("");
    setSexo(0);
    setRg("");
    setDataNascimento("");
    setEmail("");
    setTelefoneResidencial("");
    setTelefoneComercial("");
    setTelefoneRecado("");
    setCelular("");
  };

  const handleClose = () => {
    resetFields();
    onHide();
  };

  useEffect(() => {
    // Log para depuração
    if (dependenteData) {
      // Atualiza os estados de forma segura com valores padrão
      setCpfCin(dependenteData.cpfCin || "");
      setTelefoneResidencial(dependenteData.telefoneResidencial || "");
      setTelefoneComercial(dependenteData.telefoneComercial || "");
      setTelefoneRecado(dependenteData.telefoneRecado || "");
      setCelular(dependenteData.celular || "");
      setEmail(dependenteData.email || "");
      setRg(dependenteData.rg || "");

      // Validação e formatação da data de nascimento
      const dataValida =
        dependenteData.dataNascimento &&
        moment(dependenteData.dataNascimento).isValid();
      setDataNascimento(
        dataValida
          ? moment(dependenteData.dataNascimento).format("YYYY-MM-DD")
          : ""
      );
    } else {
      // Reseta os campos caso `dependenteData` seja nulo ou indefinido
      resetFields();
    }
  }, [dependenteData]);

  useEffect(() => {
    const fetchContribuinteNome = async () => {
      try {
        const response = await contribuinteService.getContribuinteById(
          idContribuinte
        );
        setNomeContribuinte(response.nome || ""); // Acessa `nome` diretamente da resposta
      } catch (error) {
        console.error("Erro ao buscar contribuinte:", error);
        Swal.fire(
          "Erro",
          "Erro ao buscar informações do contribuinte.",
          "error"
        );
      }
    };

    fetchContribuinteNome();

    if (dependenteData) {
      setNome(dependenteData.nome);
      setCpfCin(dependenteData.cpfCin ?? "");
      setSexo(dependenteData.sexo);
      setRg(dependenteData.rg);
      if (dependenteData.dataNascimento) {
        const parsedDate = new Date(dependenteData.dataNascimento);
        if (!isNaN(parsedDate.getTime())) {
          setDataNascimento(parsedDate.toISOString().split("T")[0]);
        } else {
          console.error("Data inválida:", dependenteData.dataNascimento);
        }
      } else {
        console.error("Data de nascimento não fornecida");
      }

      setEmail(dependenteData.email);
      setTelefoneResidencial(dependenteData.telefoneResidencial);
      setTelefoneComercial(dependenteData.telefoneComercial);
      setTelefoneRecado(dependenteData.telefoneRecado);
      setCelular(dependenteData.celular);
    } else {
      resetFields();
    }
  }, [dependenteData, idContribuinte]);

  useEffect(() => {
    if (!isFormSubmitted) {
      camposObrigatorios.forEach((campo) => {
        const inputElement = document.getElementById(
          campo.nome
        ) as HTMLInputElement;
        inputElement?.classList.remove("input-error"); // Remove a classe de erro sempre que o modal for aberto
      });
    }
  }, [isFormSubmitted]);

  const handleInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    switch (name) {
      case "cpfCin":
        const formattedCpf = value ? formatCPF(value) : "";
        setCpfCin(formattedCpf);
        if (value.length >= 14 && !isRequest) {
          if (isValidCPF(formattedCpf)) {
            setIsRequest(true);
            Swal.fire({
              title: "Aguarde",
              text: "Validando CPF...",
              icon: "info",
              showConfirmButton: false,
              allowOutsideClick: false,
            });
            await dependenteService.searchCpf(formattedCpf).then((response) => {
              Swal.close();
              if (response.status) {
                Swal.fire({
                  title: "CPF Existente",
                  text: response.message,
                  icon: "error",
                  confirmButtonText: "Ok",
                  allowOutsideClick: false,
                }).then(() => {
                  setInvalidFields((prev) => {
                    if (!prev.includes("cpfCin")) return [...prev, "cpfCin"];
                    return prev;
                  });
                });
              } else {
                setInvalidFields((prev) =>
                  prev.filter((field) => field !== "cpfCin")
                );
              }
              setIsRequest(false);
            });
          } else {
            Swal.fire({
              title: "CPF Inválido",
              text: "Por favor, insira um CPF válido.",
              icon: "warning",
              confirmButtonText: "Ok",
              allowOutsideClick: false,
            });
            setInvalidFields((prev) => {
              if (!prev.includes("cpfCin")) return [...prev, "cpfCin"];
              return prev;
            });
          }
        } else if (!value) {
          setInvalidFields((prev) =>
            prev.filter((field) => field !== "cpfCin")
          );
        }
        break;
      case "rg":
        setRg(formatRG(value));
        break;
      case "celular":
        setCelular(formatPhoneNumber(value));
        break;
      case "telefoneResidencial":
        setTelefoneResidencial(formatPhoneNumber(value));
        break;
      case "telefoneComercial":
        setTelefoneComercial(formatPhoneNumber(value));
        break;
      case "telefoneRecado":
        setTelefoneRecado(formatPhoneNumber(value));
        break;
      default:
        break;
    }
  };

  const formatCPF = (value: string) => {
    value = value.replace(/\D/g, "");
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    return value;
  };

  const formatRG = (value: string) => {
    value = value.replace(/\D/g, "");
    value = value.replace(/(\d{2})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    return value;
  };

  const formatPhoneNumber = (value: string): string => {
    // Remove caracteres não numéricos
    value = value.replace(/\D/g, "");

    if (value.length === 10) {
      // Formato para telefones fixos (10 dígitos)
      return value.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
    } else if (value.length === 11) {
      // Formato para celulares (11 dígitos)
      return value.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
    }

    // Retorna o valor original formatado parcialmente, se incompleto
    return value;
  };

  return (
    <ModalContainer show={show}>
      <ModalContent width={50}>
        <Button
          type="button"
          schemaButton="x-close"
          action={() => onHide()}
          icon={<IoCloseSharp size={30} />}
          position="absolute"
          top={10}
          right={10}
        />
        <ModalHeader>
          <BlueTitle>Dados pessoais do dependente</BlueTitle>
        </ModalHeader>
        <ModalBody>
          <Label>
            Contribuinte titular:
            {!nomeContribuinte ? (
              <LoadingOverlay>
                <LoadingGif src={loadSCGif} alt="Carregando..." />
              </LoadingOverlay>
            ) : (
              <ContribuinteName>{nomeContribuinte}</ContribuinteName>
            )}
          </Label>
          <RequiredFields>
            (*) Indicativos de campos obrigatórios
          </RequiredFields>
          <Row>
            <Col>
              <Label htmlFor="nome">
                Nome <TextError>*</TextError>
              </Label>
              <InputComponent
                type="text"
                id="nome" // ID correspondente ao nome no array camposObrigatorios
                name="nome" // Nome correspondente ao estado
                value={nome ? nome.toUpperCase() : nome.toUpperCase()}
                onChange={(e) => {
                  setNome(e.target.value.toUpperCase());
                  clearError(e);
                }}
                placeholder="Insira o nome completo"
                color="#333"
                border="1px solid #ccc"
                maxLength={155}
                disabled={isRequired}
                errorMessage={
                  invalidFields.includes("nome") ? "Preencha o campo Nome" : ""
                }
              />
            </Col>
            <Col>
              <Label htmlFor="cpfCin">CPF/CIN</Label>
              <InputComponent
                type="text"
                id="cpfCin" // ID correspondente ao nome no array camposObrigatorios
                name="cpfCin" // Nome correspondente ao estado
                value={cpfCin ? cpfCin : ""} // Exibe o CPF formatado
                onChange={(e) => handleInputChange(e)} // Chama a função para formatar o CPF ao digitar
                placeholder="000.000.000-00" // Placeholder já com a máscara
                color="#333"
                border="1px solid #ccc"
                maxLength={14} // Limite máximo de caracteres com a máscara
                disabled={isRequired}
                errorMessage={
                  invalidFields.includes("cpfCin")
                    ? "Verifique se o preenchimento do campo CPF/CIN está correto"
                    : ""
                }
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Label htmlFor="rg">RG</Label>
              <InputComponent
                type="text"
                id="inputRg"
                name="rg"
                value={rg}
                onChange={(e) => handleInputChange(e)}
                placeholder="00.000.000-0"
                maxLength={12}
                color="#333"
                border="1px solid #ccc"
                disabled={isRequired}
              />
            </Col>
            <Col>
              <Label htmlFor="dataNascimento">Data de nascimento</Label>
              <InputComponent
                type="date"
                id="dataNascimento" // ID correspondente ao nome no array camposObrigatorios
                name="dataNascimento" // Nome correspondente ao estado
                value={dataNascimento ? dataNascimento : ""} // Garante que o valor inicial seja uma string
                onChange={(e) => {
                  const selectedDate = e.target.value; // Captura o valor do input
                  setDataNascimento(selectedDate); // Atualiza o estado diretamente
                }}
                placeholder="00/00/0000"
                color="#333"
                border="1px solid #ccc"
                disabled={isRequired}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Label htmlFor="sexo">Sexo</Label>
              <SelectComponent
                color="#333"
                placeholder="Selecione"
                id="sexo"
                name="sexo"
                value={sexo ? sexo.toString() : "0"}
                onChange={(e) => setSexo(Number(e.target.value))}
                disabled={isRequired}
                options={[
                  { value: "1", label: "Masculino" },
                  { value: "2", label: "Feminino" },
                  { value: "3", label: "Outros" },
                ]}
              />
            </Col>
            <Col>
              <Label htmlFor="email">E-mail</Label>
              <InputComponent
                type="text"
                id="inputEmail"
                name="email"
                value={email ? email.toLowerCase() : email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@email.com"
                color="#333"
                border="1px solid #ccc"
                disabled={isRequired}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Label htmlFor="celular">Celular</Label>
              <InputComponent
                type="text"
                id="inputCelular"
                name="celular"
                value={celular} // Exibe o valor formatado
                onChange={handleInputChange} // Chama a função para formatar o celular ao digitar
                placeholder="(00) 00000-0000" // Placeholder com a máscara
                color="#333"
                border="1px solid #ccc"
                maxLength={15} // Limita o input a 15 caracteres
                disabled={isRequired}
              />
            </Col>
            <Col>
              <Label htmlFor="telefoneResidencial">
                Telefone residencial (opcional)
              </Label>
              <InputComponent
                type="text"
                id="inputTelefoneResidencial"
                name="telefoneResidencial"
                value={telefoneResidencial} // Exibe o valor formatado
                onChange={handleInputChange}
                placeholder="(00) 0000-0000" // Placeholder com a máscara
                color="#333"
                border="1px solid #ccc"
                maxLength={14} // Limita o input a 14 caracteres (com a máscara)
                disabled={isRequired}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Label htmlFor="telefoneComercial">Telefone comercial</Label>
              <InputComponent
                type="text"
                id="inputTelefoneComercial"
                name="telefoneComercial"
                value={telefoneComercial} // Exibe o valor formatado
                onChange={handleInputChange}
                placeholder="(00) 00000-0000" // Placeholder com a máscara
                color="#333"
                border="1px solid #ccc"
                maxLength={15} // Limita o input a 14 caracteres (com a máscara)
                disabled={isRequired}
              />
            </Col>
            <Col>
              <Label htmlFor="telefoneRecado">Telefone recado</Label>
              <InputComponent
                type="text"
                id="inputTelefoneRecado"
                name="telefoneRecado"
                value={telefoneRecado} // Exibe o valor formatado
                onChange={handleInputChange}
                placeholder="(00) 00000-0000" // Placeholder com a máscara
                color="#333"
                border="1px solid #ccc"
                maxLength={15} // Limita o input a 15 caracteres (com a máscara)
                disabled={isRequired}
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button schemaButton="out-primary" action={handleClose}>
            Cancelar
          </Button>
          <Button
            schemaButton="full-primary"
            action={handleSubmit}
            disabled={isFormSubmitted || invalidFields.length > 0}
          >
            {dependenteData ? "Atualizar dependente" : "Cadastrar dependente"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalContainer>
  );
};

export default DependenteModal;
